import React, { useState, useEffect } from "react";
import { AcmeCoOperationErrorIcon, TooltipsIcon } from "../../assets/icons";
import axios from "axios";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { reqToUpdateSettings } from "../../redux/slices.js/settings/settingsSlice";

const DunningManagement = ({ settings = [] }) => {
  const dispatch = useDispatch();
  // Convert API array → object { name: value }
  const initialState = settings.reduce((acc, s) => {
    acc[s.name] = s.value;
    return acc;
  }, {});

  const [formValues, setFormValues] = useState({
    payment_retry_attempts: "",
    apply_connection_fee: false,
    send_email_payment_reminder: false,
    send_sms_payment_reminder: false,
    auto_suspend_after_grace_period: false,
    ...initialState,
  });

  const dunningToggles = [
    {
      id: "apply_connection_fee",
      label: "Apply collection fees to overdue accounts",
    },
  ];

  const reminderToggles = [
    { id: "send_email_payment_reminder", label: "Send email payment reminder" },
    { id: "send_sms_payment_reminder", label: "Send SMS payment reminders" },
    {
      id: "auto_suspend_after_grace_period",
      label: "Auto-suspend after grace period",
    },
  ];

  const handleToggle = (id) => {
    setFormValues((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleChange = (field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveChanges = () => {
    const payload = Object.entries(formValues).map(([name, value]) => ({
      name,
      value: value instanceof Date ? value.toISOString() : value,
    }));

    dispatch(
      reqToUpdateSettings({
        data: payload,
        onSuccess: (response) => {
          // any additional success logic here
        },
        onFailure: (error) => {
          console.error("Failed to update settings:", error);
          // any additional failure logic here
        },
      })
    );
  };

  return (
    <>
      <div className="space-y-6">
        <div className="bg-[#EDF7FF] rounded-xl sm:p-6 p-3">
          <div className="flex flex-col xl:flex-row md:items-start gap-4 md:justify-between">
            <div className="flex items-center gap-3">
              <span className="min-[40px]">{AcmeCoOperationErrorIcon}</span>
              <div>
                <span className="text-[16px] font-semibold mb-2 block">
                  Payment Collection Policies
                </span>
                <p className="text-[16px] text-gray-500 text-sm mb-0">
                  Configure how the system handles failed payments and payment
                  recovery across all tenants
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-4 md:gap-6 md:grid-cols-2">
          <div className="form-group">
            <label>
              Payment Retry Attempts <span>*</span>
            </label>
            <div className="relative">
              <input
                type="number"
                placeholder="3 attempts recommended"
                className="pe-5"
                value={formValues.payment_retry_attempts}
                onChange={(e) =>
                  handleChange("payment_retry_attempts", e.target.value)
                }
              />
              <span className="absolute right-3 top-3 h-5 w-5 text-gray-500 pointer-events-none">
                {TooltipsIcon}
              </span>
            </div>
          </div>
        </div>

        {/* Dunning Toggles */}
        <div className="grid gap-4">
          {dunningToggles.map((toggle) => (
            <div
              key={toggle.id}
              className="flex items-center justify-start gap-4"
            >
              <button
                type="button"
                role="switch"
                aria-checked={formValues[toggle.id]}
                onClick={() => handleToggle(toggle.id)}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  formValues[toggle.id] ? "bg-[#4285F4]" : "bg-[#E5E7EB]"
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    formValues[toggle.id]
                      ? "translate-x-[10px]"
                      : "translate-x-0"
                  }`}
                />
              </button>
              <span className="text-black text-span">{toggle.label}</span>
            </div>
          ))}
        </div>

        {/* Reminder Toggles */}
        <div className="form-group">
          <label className="!text-black mb-3">Reminder Settings</label>
          <div className="grid gap-4">
            {reminderToggles.map((toggle) => (
              <div
                key={toggle.id}
                className="flex items-center justify-start gap-4"
              >
                <button
                  type="button"
                  role="switch"
                  aria-checked={formValues[toggle.id]}
                  onClick={() => handleToggle(toggle.id)}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    formValues[toggle.id] ? "bg-[#4285F4]" : "bg-[#E5E7EB]"
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      formValues[toggle.id]
                        ? "translate-x-[10px]"
                        : "translate-x-0"
                    }`}
                  />
                </button>
                <span className="text-black text-span">{toggle.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-[#EDF7FF] rounded-xl sm:p-6 p-3">
          <div className="flex flex-col xl:flex-row md:items-start gap-4 md:justify-between">
            <div className="flex items-center gap-3">
              <span className="min-[40px]">{AcmeCoOperationErrorIcon}</span>
              <div>
                <span className="text-[16px] font-semibold mb-2 block">
                  Dunning Best Practices
                </span>
                <p className="text-[16px] text-gray-500 text-sm mb-0">
                  A balanced approach with 3 retry attempts and 7-day grace
                  period typically provides the best recovery rates while
                  maintaining customer relationships.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={`mt-auto flex gap-3 pt-6 justify-end flex-wrap`}>
        <button type="button" className="next-btn" onClick={handleSaveChanges}>
          Save Dunning policies
        </button>
      </div>
    </>
  );
};

export default DunningManagement;
